import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { cidApi } from '@/api/cid';
import { useDebounce } from './use-debounce';
import type { CIDEntry, CIDPaginatedResponse } from '@/types/cid';

const PAGE_SIZE = 50;
const MIN_QUERY_LENGTH = 2;

interface UseCidSearchParams {
    query: string;
    source?: 'cid10' | 'cid11';
}

export function useCidSearch({ query, source = 'cid11' }: UseCidSearchParams) {
    const debouncedQuery = useDebounce(query, 300);
    const activeQuery = debouncedQuery.trim();

    // A single character produces noisy, mostly-irrelevant matches; wait for a
    // real term. An empty query still browses the full list.
    const isQueryTooShort = activeQuery.length > 0 && activeQuery.length < MIN_QUERY_LENGTH;

    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
        useInfiniteQuery<
            CIDPaginatedResponse,
            Error,
            InfiniteData<CIDPaginatedResponse>,
            string[],
            number
        >({
            queryKey: ['cid-search', activeQuery, source],
            queryFn: ({ pageParam }) =>
                cidApi.search({
                    q: activeQuery || undefined,
                    source,
                    page: Number(pageParam) || 1,
                    pageSize: PAGE_SIZE,
                }),
            initialPageParam: 1,
            getNextPageParam: (lastPage, allPages) => {
                if (!lastPage.pagination.hasMore) return undefined;
                return allPages.length + 1;
            },
            staleTime: 1000 * 60 * 5,
            enabled: !isQueryTooShort,
        });

    const entries = useMemo<CIDEntry[]>(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => page.data);
    }, [data]);

    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const sentinelCallbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            sentinelRef.current = node;

            if (!node) return;

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                    }
                },
                { threshold: 0 },
            );

            observerRef.current.observe(node);
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    );

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return {
        entries,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        error,
        activeQuery,
        isQueryTooShort,
        sentinelRef: sentinelCallbackRef,
    };
}
